import React, { useEffect, useState } from 'react';
import { useItemValue, useItemSection } from '@/hooks/tab';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import { useItem } from '@/hooks/item';
import { ItemTab } from '@/types/common';
import { Heading } from '@/components/atoms/Section';
import { ErrorView, ServiceErrorView } from '@/components/atoms/Error';
import Tabs from '@/components/molecules/common/Tabs';
import Item from '@/components/organisms/item/Item';
import Order from '@/components/organisms/order/Order';
import { ClientIdView } from '@/components/organisms/client/Client';
import Histo from '@/components/organisms/item/Histo';
import OmsReturnStatus from '@/components/organisms/item/OmsReturnStatus';
import styles from './Common.module.scss';

const View: React.FC = () => {
  const [inputValue] = useItemValue();
  return inputValue === '' ? <></> : <ItemStandalone id={Number(inputValue)} />;
};

export const ItemStandalone: React.FC<{ id: number }> = ({ id }) => {
  const { t } = useTranslation();

  const [section] = useItemSection();

  const [tab, setTab] = useState(ItemTab.INFO);
  const data = useItem(id);

  const tabLabels = [
    t('item.tabs.info'),
    t('item.tabs.order'),
    t('item.tabs.seller'),
    t('item.tabs.buyer'),
    t('item.tabs.histo'),
    t('item.tabs.return_status'),
  ];

  const onChangeTab = (_: React.SyntheticEvent, newTab: number) => {
    setTab(newTab);
  };

  useEffect(() => {
    if (!section) setTab(ItemTab.INFO);
    else {
      switch (section.toUpperCase()) {
        case ItemTab[ItemTab.HISTO]:
          setTab(ItemTab.HISTO);
          break;
        case ItemTab[ItemTab.RETURN_STATUS]:
          setTab(ItemTab.RETURN_STATUS);
          break;
        default:
          break;
      }
    }
  }, [section]);

  const skeleton = (
    <Skeleton
      height={100}
      variant="rounded"
    />
  );

  const order =
    data.kind === 'Called' ? (
      skeleton
    ) : data.kind === 'Loaded' && data.data.orderId && data.data.orderId !== '0' ? (
      <Order id={Number(data.data.orderId)} />
    ) : data.kind === 'Loaded' && data.data.soldDate === '0000-00-00 00:00:00' ? (
      <ErrorView message={t('common.item_not_sold')} />
    ) : (
      <ErrorView message={t('order.not_found')} />
    );

  const seller =
    data.kind === 'Called' ? (
      skeleton
    ) : data.kind === 'Loaded' && data.data.sellerId && data.data.sellerId !== '0' ? (
      <ClientIdView
        id={Number(data.data.sellerId)}
        isBuyer={false}
      />
    ) : (
      <ErrorView message={t('client.not_found')} />
    );

  const buyer =
    data.kind === 'Called' ? (
      skeleton
    ) : data.kind === 'Loaded' && data.data.buyerId && data.data.buyerId !== '0' ? (
      <ClientIdView
        id={Number(data.data.buyerId)}
        isBuyer={true}
      />
    ) : data.kind === 'Loaded' && data.data.soldDate === '0000-00-00 00:00:00' ? (
      <ErrorView message={t('common.item_not_sold')} />
    ) : (
      <ErrorView message={t('client.not_found')} />
    );

  const getTabContents = () => {
    if (data.kind === 'Loaded') {
      switch (tab) {
        case ItemTab.INFO:
          return (
            <Item
              id={id}
              sellerId={Number(data.data.sellerId)}
            />
          );
        case ItemTab.ORDER:
          return order;
        case ItemTab.SELLER:
          return seller;
        case ItemTab.BUYER:
          return buyer;
        case ItemTab.HISTO:
          return <Histo item={data.data} />;
        case ItemTab.RETURN_STATUS:
          return <OmsReturnStatus item={data.data} />;
        default:
          return null;
      }
    }
    return null;
  };

  return (
    <div
      className={styles.container}
      data-cy={`PageContent-item__${id}`}
    >
      <div className={styles.headingItem}>
        <Heading label={`${t('sidebar.item')} #${id}`} />
      </div>
      {data.kind === 'Called' && <div className={styles.tabsContainer}>{skeleton}</div>}
      {data.kind === 'Error' && (
        <div className={styles.tabsContainer}>
          <ServiceErrorView error={data.error} />
        </div>
      )}
      {data.kind === 'Loaded' && (
        <>
          <div className={styles.tabsContainer}>
            <div
              className={styles.tabs}
              data-cy={`PageContent-item__${id}_tabs`}
            >
              <Tabs
                onChangeTab={onChangeTab}
                labels={tabLabels}
                current={tab}
                scrollButtons={'auto'}
                variant={'scrollable'}
              />
            </div>
          </div>
          <div
            className={styles.sectionWrapper}
            data-cy={`PageContent-item__${id}_content`}
          >
            <div className={clsx(styles.wrapper)}>{getTabContents()}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default View;
